import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, ArrowUpDown, ChevronLeft, ChevronRight, Copy, RefreshCw, Phone, PhoneOff, Mail, MailX, Trophy, Send } from "lucide-react";

const STATUS_STYLES = {
  "Nuovo": "bg-blue-100 text-blue-700",
  "Da verificare": "bg-amber-100 text-amber-700",
  "Valido": "bg-emerald-100 text-emerald-700",
  "Duplicato": "bg-orange-100 text-orange-700",
  "Non valido": "bg-rose-100 text-rose-700",
  "Archiviato": "bg-slate-200 text-slate-600",
};
const ALL = "__all__";

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function buildDateTabs() {
  const now = new Date();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minus = (n) => { const d = new Date(t); d.setDate(t.getDate() - n); return d; };
  return [
    { key: "all", label: "Tutte", range: null },
    { key: "today", label: "Oggi", range: { date_from: isoDay(t), date_to: isoDay(t) } },
    { key: "yest", label: "Ieri", range: { date_from: isoDay(minus(1)), date_to: isoDay(minus(1)) } },
    { key: "d2", label: "Altro ieri", range: { date_from: isoDay(minus(2)), date_to: isoDay(minus(2)) } },
    { key: "d7", label: "Ultimi 7 giorni", range: { date_from: isoDay(minus(6)), date_to: isoDay(t) } },
    { key: "d15", label: "Ultimi 15 giorni", range: { date_from: isoDay(minus(14)), date_to: isoDay(t) } },
  ];
}

export default function Leads({ mode = "arrivals" }) {
  const navigate = useNavigate();
  const isForward = mode === "forwards";
  const [searchParams, setSearchParams] = useSearchParams();
  const presenceFilter = searchParams.get("presence") || null;
  const customerBefore = searchParams.get("customer_before") === "true";
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL);
  const [courseId, setCourseId] = useState(ALL);
  const [duplicate, setDuplicate] = useState(ALL);
  const [commercial, setCommercial] = useState(ALL);
  const [called, setCalled] = useState(ALL);
  const [channel, setChannel] = useState(ALL);
  const [dateTab, setDateTab] = useState(isForward ? "today" : "all");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const dateTabs = useMemo(buildDateTabs, []);
  const activeRange = dateTabs.find((t) => t.key === dateTab)?.range || null;

  const { data: courses } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  const params = {
    sort_by: isForward ? "assigned_at" : "received_at",
    sort_dir: sortDir,
    page,
    page_size: pageSize,
    ...(search ? { search } : {}),
    ...(status !== ALL ? { status } : {}),
    ...(courseId !== ALL ? { course_id: courseId } : {}),
    ...(duplicate === "recurring" ? { recurring: true } : {}),
    ...(duplicate === "recurring_same" ? { recurring_same_course: true } : {}),
    ...(duplicate === "recurring_other" ? { recurring_same_course: false } : {}),
    ...(duplicate === "only_dup" ? { duplicate: true } : {}),
    ...(duplicate === "all" ? { include_duplicates: true } : {}),
    ...(commercial !== ALL ? { commercial } : {}),
    ...(called === "yes" ? { called: true } : {}),
    ...(called === "no" ? { called: false } : {}),
    ...(channel !== ALL ? { channel } : {}),
    ...(presenceFilter ? { presence: presenceFilter } : {}),
    ...(customerBefore ? { customer_before: true } : {}),
    ...(isForward ? { date_field: "assigned_at", assigned: true, assigned_source: "inoltro" } : {}),
    ...(activeRange || {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["leads", params],
    queryFn: async () => (await api.get("/leads", { params })).data,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const statuses = data?.statuses || Object.keys(STATUS_STYLES);

  const fmtDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  };

  return (
    <div className="space-y-5" data-testid="leads-page">
      {(presenceFilter || customerBefore) && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm" data-testid="active-url-filter">
          <span className="font-semibold text-blue-800">
            {customerBefore ? "Filtro: Già clienti (avevano già un acquisto)" : "Filtro: Lead vecchi (già presenti in Pipedrive)"}
          </span>
          <button
            data-testid="clear-url-filter"
            onClick={() => setSearchParams({})}
            className="ml-auto rounded-full border border-blue-300 bg-white px-3 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
          >Rimuovi filtro</button>
        </div>
      )}
      {isForward && (
        <p className="text-sm text-muted-foreground" data-testid="forwards-intro">
          Richieste <span className="font-semibold text-blue-700">inoltrate al commerciale</span> nel periodo (per data di inoltro da dem@italiacorsi.it). Usa i filtri per vedere, ad esempio, cosa è stato inoltrato oggi a un commerciale e se è stato gestito.
        </p>
      )}
      {/* Tab periodo */}
      <div className="flex flex-wrap items-center gap-2" data-testid="lead-date-tabs">
        {dateTabs.map((t) => (
          <button
            key={t.key}
            data-testid={`lead-date-tab-${t.key}`}
            onClick={() => { setDateTab(t.key); setPage(1); }}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              dateTab === t.key
                ? "bg-blue-600 text-white shadow-sm"
                : "border border-border bg-white text-muted-foreground hover:bg-secondary"
            }`}
          >{t.label}</button>
        ))}
        {data && (
          <span className="ml-auto text-sm text-muted-foreground" data-testid="lead-date-count">
            {data.total} richieste{isForward ? " inoltrate" : ""}{dateTab !== "all" ? " nel periodo" : ""}
          </span>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="lead-search-input"
              placeholder="Cerca per nome, email, telefono, corso…"
              className="pl-9"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-44" data-testid="lead-status-filter">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti gli stati</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={courseId} onValueChange={(v) => { setCourseId(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-52" data-testid="lead-course-filter">
              <SelectValue placeholder="Corso" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti i corsi</SelectItem>
              {(courses || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.official_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={duplicate} onValueChange={(v) => { setDuplicate(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-52" data-testid="lead-duplicate-filter">
              <SelectValue placeholder="Duplicati" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Unici + recidivi</SelectItem>
              <SelectItem value="recurring">Tutti i recidivi</SelectItem>
              <SelectItem value="recurring_same">Recidivi · stesso corso</SelectItem>
              <SelectItem value="recurring_other">Recidivi · altro corso</SelectItem>
              <SelectItem value="only_dup">Solo duplicati</SelectItem>
              <SelectItem value="all">Mostra tutto</SelectItem>
            </SelectContent>
          </Select>
          <Select value={commercial} onValueChange={(v) => { setCommercial(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-44" data-testid="lead-commercial-filter">
              <SelectValue placeholder="Commerciale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti i commerciali</SelectItem>
              <SelectItem value="Carla Catena">Carla Catena</SelectItem>
              <SelectItem value="Luca Stanzani">Luca Stanzani</SelectItem>
              <SelectItem value="Cristina Navarra">Cristina Navarra</SelectItem>
            </SelectContent>
          </Select>
          <Select value={channel} onValueChange={(v) => { setChannel(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-40" data-testid="lead-channel-filter">
              <SelectValue placeholder="Canale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti i canali</SelectItem>
              <SelectItem value="Facebook">Facebook</SelectItem>
              <SelectItem value="LinkedIn">LinkedIn</SelectItem>
              <SelectItem value="Sito web">Sito web</SelectItem>
              <SelectItem value="Altro">Altro</SelectItem>
            </SelectContent>
          </Select>
          <Select value={called} onValueChange={(v) => { setCalled(v); setPage(1); }}>
            <SelectTrigger className="w-full lg:w-40" data-testid="lead-called-filter">
              <SelectValue placeholder="Chiamato?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Chiamati e non</SelectItem>
              <SelectItem value="yes">Solo chiamati</SelectItem>
              <SelectItem value="no">Mai chiamati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60">
                <TableHead>
                  <button
                    data-testid="sort-date-button"
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider"
                    onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  >
                    Data <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                {isForward && <TableHead className="text-xs font-semibold uppercase tracking-wider">Inoltrata il</TableHead>}
                {isForward && <TableHead className="text-xs font-semibold uppercase tracking-wider">Ri-inoltro</TableHead>}
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Nome</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Telefono</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Professione</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Corso</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Canale</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Campagna</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Stato</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Presenza</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Tipo</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Chiamato?</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Email inviata?</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: isForward ? 15 : 14 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data?.items?.length ? (
                data.items.map((lead) => (
                  <TableRow
                    key={lead.id}
                    data-testid={`lead-row-${lead.id}`}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDate(lead.received_at)}</TableCell>
                    {isForward && (
                      <TableCell className="whitespace-nowrap text-sm">
                        {lead.pipedrive?.funnel?.assigned_at ? (
                          <div>
                            <div className="text-muted-foreground">{fmtDate(lead.pipedrive.funnel.assigned_at)}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {lead.pipedrive.funnel.commercial || "—"}
                              {lead.pipedrive.funnel.assigned_source === "inoltro" ? " · inoltro" : lead.pipedrive.funnel.assigned_source === "diretto" ? " · diretto" : ""}
                            </div>
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    {isForward && <TableCell><ReforwardCell funnel={lead.pipedrive?.funnel} fmt={fmtDate} /></TableCell>}
                    <TableCell className="text-sm font-medium">{lead.normalized?.full_name || "-"}</TableCell>
                    <TableCell className="text-sm">{lead.normalized?.email || "-"}</TableCell>
                    <TableCell className="text-sm">{lead.normalized?.phone || "-"}</TableCell>
                    <TableCell className="text-sm">{lead.normalized?.profession || "-"}</TableCell>
                    <TableCell className="text-sm">
                      {lead.course_name || <span className="text-rose-600">Non riconosciuto</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{lead.channel || "Altro"}</span>
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate text-sm text-muted-foreground">{lead.normalized?.campaign || "-"}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_STYLES[lead.status] || "bg-slate-100"} rounded-full font-semibold hover:bg-inherit`}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell><PipedriveCell pv={lead.pipedrive} /></TableCell>
                    <TableCell>
                      {lead.is_recurring ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${lead.recurring_same_course ? "bg-violet-100 text-violet-700" : "bg-indigo-100 text-indigo-700"}`} title={lead.recurring_same_course ? "Ha già chiesto QUESTO stesso corso in passato — da richiamare subito" : "Ha già chiesto un ALTRO corso in passato — da attenzionare"}>
                          <RefreshCw className="h-3 w-3" /> {lead.recurring_same_course ? "Recidivo · stesso corso" : "Recidivo · altro corso"}
                        </span>
                      ) : lead.is_duplicate ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                          <Copy className="h-3 w-3" /> Duplicato
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><CalledCell calls={lead.calls} /></TableCell>
                    <TableCell><EmailedCell flag={lead.email_flag} /></TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isForward ? 15 : 14} className="py-16 text-center text-sm text-muted-foreground">
                    Nessun lead trovato. Sincronizza Gmail dalla pagina Integrazioni.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {data?.total ?? 0} lead • Pagina {page} di {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="prev-page-button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="next-page-button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function EmailedCell({ flag }) {
  if (!flag || !flag.checked) return <span className="text-xs text-muted-foreground" title="In verifica…">—</span>;
  if (flag.emailed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">
        <Mail className="h-3 w-3" /> Sì
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
      <MailX className="h-3 w-3" /> No
    </span>
  );
}

function CalledCell({ calls }) {
  if (!calls) return <span className="text-xs text-muted-foreground">—</span>;
  if (calls.called) {
    const sec = calls.matched_secondary;
    const secNums = (calls.secondary_numbers || []).join(", ");
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${sec ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
        title={`${calls.total} chiamata/e${calls.last_agent ? " · ultima: " + calls.last_agent : ""}${sec && secNums ? " · su n. Pipedrive: " + secNums : ""}`}
      >
        <Phone className="h-3 w-3" /> {sec ? "Chiamato · n. Pipedrive" : "Chiamato"}{calls.total > 1 ? ` (${calls.total})` : ""}
      </span>
    );
  }
  if (calls.no_call_system) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500" title="Il commerciale non usa il sistema di chiamate SalesTrail">
        Senza sistema chiamate
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
      <PhoneOff className="h-3 w-3" /> Mai chiamato
    </span>
  );
}

function WonBadge({ deals }) {
  if (!deals || deals.length === 0) return null;
  const title = deals[0].title || "Affare vinto";
  return (
    <div
      data-testid="lead-won-badge"
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
      title={deals.map((d) => d.title).filter(Boolean).join(", ")}
    >
      <Trophy className="h-3 w-3" /> Vinto{title ? ` · ${title}` : ""}
    </div>
  );
}

function PipedriveCell({ pv }) {
  if (!pv) return <span className="text-xs text-muted-foreground">In attesa sync</span>;
  const deals = pv.won_deals || [];
  return (
    <div>
      {pv.presence === "gia_presente" ? (
        <>
          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Già presente</span>
          {pv.presence_owner && <div className="mt-0.5 text-[11px] text-muted-foreground">{pv.presence_owner}</div>}
        </>
      ) : (
        <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">Nuovo</span>
      )}
      <WonBadge deals={deals} />
    </div>
  );
}

function ReforwardCell({ funnel, fmt }) {
  const f = funnel || {};
  const owner = f.current_owner_name;
  const recv = f.commercial;
  if (!owner || !recv || owner === recv) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-0.5" data-testid="reforward-cell">
      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" title={`Cliente di proprietà di ${owner}`}>
        <Send className="h-3 w-3" /> → {owner}
      </span>
      {f.owner_changed && f.owner_changed_at
        ? <span className="text-[11px] text-emerald-700">passato il {fmt(f.owner_changed_at)}</span>
        : <span className="text-[11px] text-amber-600">da ri-inoltrare</span>}
    </div>
  );
}
